import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { App } from '@capacitor/app';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { StatusBar, Style } from '@capacitor/status-bar';
import type { PluginListenerHandle } from '@capacitor/core';
import { NavigationHistoryService } from '../navigation/navigation-history.service';
import { TenantService } from '../../services/tenant.service';
import { PlatformService } from './platform.service';

@Injectable({
  providedIn: 'root'
})
export class CapacitorRuntimeService {
  private initialized = false;
  private readonly listeners: PluginListenerHandle[] = [];

  constructor(
    private readonly platformService: PlatformService,
    private readonly router: Router,
    private readonly location: Location,
    private readonly navigationHistoryService: NavigationHistoryService,
    private readonly tenantService: TenantService
  ) {}

  async initialize(): Promise<void> {
    if (this.initialized || !this.isCapacitorRuntime()) {
      return;
    }

    this.initialized = true;

    await Keyboard.setResizeMode({ mode: KeyboardResize.Body }).catch(() => undefined);
    await StatusBar.setOverlaysWebView({ overlay: false }).catch(() => undefined);
    await StatusBar.setStyle({ style: Style.Light }).catch(() => undefined);

    const keyboardDidShow = await Keyboard.addListener('keyboardDidShow', () => {
      document.body.classList.add('keyboard-open');
    });
    this.listeners.push(keyboardDidShow);

    const keyboardDidHide = await Keyboard.addListener('keyboardDidHide', () => {
      document.body.classList.remove('keyboard-open');
    });
    this.listeners.push(keyboardDidHide);

    const backButton = await App.addListener('backButton', ({ canGoBack }) => {
      const url = this.normalizeUrl(this.router.url);

      if (url === '/' || url === '/dashboard' || url === '/login') {
        App.exitApp();
        return;
      }

      if (canGoBack || this.navigationHistoryService.hasPreviousEntry()) {
        this.location.back();
        return;
      }

      this.router.navigateByUrl('/dashboard');
    });
    this.listeners.push(backButton);

    const appUrlOpen = await App.addListener('appUrlOpen', ({ url }) => {
      this.handleDeepLink(url);
    });
    this.listeners.push(appUrlOpen);

    const launchUrl = await App.getLaunchUrl().catch(() => null);
    if (launchUrl?.url) {
      this.handleDeepLink(launchUrl.url);
    }
  }

  private isCapacitorRuntime(): boolean {
    return this.platformService.isBrowser() && this.platformService.getRuntimePlatform() === 'capacitor';
  }

  private handleDeepLink(url: string): void {
    try {
      const parsedUrl = new URL(url);
      const tenantFromHost = this.tenantService.extractTenantFromHost(parsedUrl.hostname);
      if (tenantFromHost) {
        this.tenantService.setCurrentTenantSchema(tenantFromHost);
      }

      const pathname = parsedUrl.pathname && parsedUrl.pathname !== '/'
        ? parsedUrl.pathname
        : parsedUrl.hostname
          ? `/${parsedUrl.hostname}`
          : '/';
      const nextUrl = `${pathname}${parsedUrl.search}${parsedUrl.hash}`;
      this.router.navigateByUrl(nextUrl);
    } catch {
      this.router.navigateByUrl('/dashboard');
    }
  }

  private normalizeUrl(url: string): string {
    return url.split('?')[0].split('#')[0] || '/';
  }
}
